import type { IsoDate, YearMonth } from '@overseer/contracts';
import { addDays, shiftMonth } from './time';

/**
 * Report periods: the presets and the month steps the period pickers share —
 * gastronomy and hotel alike. Months are 1-based.
 */

/** The first day of the date's month. */
export const monthStart = (date: IsoDate): IsoDate => `${date.slice(0, 7)}-01` as IsoDate;

export type PeriodPreset = { id: string; from: IsoDate; to: IsoDate; labelKey: string };

/** This month so far, last month, the last 90 days and the year so far. */
export const periodPresets = (end: IsoDate): PeriodPreset[] => {
  const thisMonth = monthStart(end);
  const lastMonthEnd = addDays(thisMonth, -1);

  return [
    { id: 'm0', from: thisMonth, to: end, labelKey: 'period.thisMonth' },
    {
      id: 'm1',
      from: monthStart(lastMonthEnd),
      to: lastMonthEnd,
      labelKey: 'period.lastMonth',
    },
    { id: 'd90', from: addDays(end, -89), to: end, labelKey: 'period.last90' },
    { id: 'ytd', from: `${end.slice(0, 4)}-01-01` as IsoDate, to: end, labelKey: 'period.ytd' },
  ];
};

/** The last day of a month; months are 1-based. */
const monthEnd = ({ year, month }: YearMonth): IsoDate =>
  new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10) as IsoDate;

const monthOf = (date: IsoDate): YearMonth => ({
  year: Number(date.slice(0, 4)),
  month: Number(date.slice(5, 7)),
});

const firstOf = ({ year, month }: YearMonth): IsoDate =>
  `${year}-${String(month).padStart(2, '0')}-01` as IsoDate;

/**
 * The whole month `delta` months from the one `from` falls in, its end capped
 * at `end` (today). Null when that month starts after `end`.
 */
export const monthStep = (
  from: IsoDate,
  delta: number,
  end: IsoDate,
): { from: IsoDate; to: IsoDate } | null => {
  const target = shiftMonth(monthOf(from), delta);
  const start = firstOf(target);
  if (start > end) return null;
  const last = monthEnd(target);
  return { from: start, to: last < end ? last : end };
};

/**
 * The month a range is exactly — the whole month, or its first day to `end`
 * when the month is still running — or null.
 */
export const wholeMonth = (from: IsoDate, to: IsoDate, end: IsoDate): YearMonth | null => {
  const month = monthOf(from);
  if (from !== firstOf(month)) return null;
  const last = monthEnd(month);
  return to === last || (to === end && end < last && to.slice(0, 7) === from.slice(0, 7)) ? month : null;
};
